import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";

/**
 * Fetches the tenant's Payment Processing Charges from the public endpoint
 * and returns a stable `computeFee(amount)` helper.
 *
 * Every mount fires its own request — the endpoint is tiny and the value
 * MUST stay current when FA edits it mid-session.
 *
 * Callers can pass an `audience` argument ("unregistered" | "guest" |
 * "member" | a member_type_id). When an override exists for that audience
 * we use it; otherwise we fall back to the base rate.
 */
export function usePaymentCharges(audience = null) {
  const [charges, setCharges] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/config/payment-charges`)
      .then((r) => { if (alive) setCharges(r.data); })
      .catch(() => { /* leave charges null so computeFee short-circuits to 0 */ });
    return () => { alive = false; };
  }, []);

  // Resolve which {type,value} rule applies for the given audience.
  const activeRule = (() => {
    if (!charges) return null;
    const overrides = charges.overrides || {};
    if (audience && overrides[audience]) return overrides[audience];
    // Member-type IDs (non-standard keys) inherit the "member" override.
    if (audience && !["unregistered", "guest", "member"].includes(audience) && overrides.member) {
      return overrides.member;
    }
    return { type: charges.type, value: charges.value };
  })();

  const computeFee = (amount) => {
    if (!activeRule || !amount || amount <= 0) return 0;
    const v = Number(activeRule.value) || 0;
    const fee = activeRule.type === "fixed" ? v : (amount * v) / 100;
    return Math.round(fee * 100) / 100;
  };

  const label = activeRule
    ? (activeRule.type === "percent"
        ? `${activeRule.value}%`
        : `flat \u20ac${Number(activeRule.value).toFixed(2)}`)
    : "\u2014";

  return { charges, activeRule, computeFee, label, ready: !!charges };
}
